/**
 * <ThemeProvider> — applies a narrow set of CSS custom properties on :root
 * from an optional brand prop.
 *
 * Phase 0 of the UI reshape narrowed this contract. The ONLY tokens a tenant
 * (or consuming app) may override are the four accent tokens:
 *   --accent, --accent-hover, --accent-foreground, --accent-dim
 *
 * Workspace colors, semantic colors (--rush, --high, --success), and
 * typography are NOT overridable — see `UX/colors.md:106-126` and
 * `UX/colors.md:120-126`.
 *
 * The high-level tenant resolution (logo, favicon, labels, Apollo fetch)
 * lives in @clearpath/tenant-branding's <BrandProvider>. That provider
 * composes this one for the accent token application.
 *
 * Kept deliberately stateless wrt non-brand concerns: a consumer app wraps
 * its tree with <ThemeProvider brand={...}> and the variables are mutated
 * on :root whenever `brand` changes. On unmount, the provider reverts the
 * variables it set so dev HMR doesn't leak.
 */
import type { JSX } from 'react';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";

/**
 * Minimal tenant-facing brand surface for CSS-variable overrides.
 *
 * Four tokens, by design — the UX contract intentionally excludes
 * workspace/semantic/typography overrides. See `UX/colors.md`.
 */
export interface ThemeBrand {
  /** Primary accent color (hex or any valid CSS color). Sets --accent. */
  accent?: string;
  /** Darker accent used for hover/active. Sets --accent-hover. */
  accentHover?: string;
  /** Foreground color that reads on top of accent fills. Sets --accent-foreground. */
  accentForeground?: string;
  /** Low-alpha tint (used for accent-dim backgrounds, focus rings). */
  accentDim?: string;
}

interface ThemeContextValue {
  brand: ThemeBrand;
}

const ThemeContext = createContext<ThemeContextValue>({ brand: {} });

export interface ThemeProviderProps {
  brand?: ThemeBrand;
  children: ReactNode;
}

/**
 * Derive the CSS variables we override from a brand. Variables we don't
 * override keep their tokens.css defaults.
 */
function buildOverrides(brand: ThemeBrand): Record<string, string> {
  const out: Record<string, string> = {};
  if (brand.accent) {
    out["--accent"] = brand.accent;
    // Derive a sensible accent-hover if the caller didn't supply one.
    if (!brand.accentHover) {
      out["--accent-hover"] = `color-mix(in srgb, ${brand.accent} 85%, black)`;
    }
    // Derive accent-dim if not supplied. color-mix is widely supported (2023+).
    if (!brand.accentDim) {
      out["--accent-dim"] = `color-mix(in srgb, ${brand.accent} 12%, transparent)`;
    }
  }
  if (brand.accentHover) out["--accent-hover"] = brand.accentHover;
  if (brand.accentForeground) {
    out["--accent-foreground"] = brand.accentForeground;
    out["--accent-text"] = brand.accentForeground; // legacy alias
  }
  if (brand.accentDim) out["--accent-dim"] = brand.accentDim;
  return out;
}

export function ThemeProvider({ brand, children }: ThemeProviderProps): JSX.Element {
  const resolvedBrand = brand ?? {};
  const overrides = useMemo(
    () => buildOverrides(resolvedBrand),
    [
      resolvedBrand.accent,
      resolvedBrand.accentHover,
      resolvedBrand.accentForeground,
      resolvedBrand.accentDim,
    ],
  );

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    // Remember prior values so we can restore on unmount / re-theme.
    const prev: Record<string, string> = {};
    for (const [key, override] of Object.entries(overrides)) {
      prev[key] = root.style.getPropertyValue(key);
      root.style.setProperty(key, override);
    }
    return () => {
      for (const key of Object.keys(overrides)) {
        if (prev[key]) root.style.setProperty(key, prev[key]);
        else root.style.removeProperty(key);
      }
    };
  }, [overrides]);

  const value = useMemo(() => ({ brand: resolvedBrand }), [resolvedBrand]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useThemeContext(): ThemeContextValue {
  return useContext(ThemeContext);
}

/**
 * Imperatively apply tenant accent branding to :root. Useful outside React
 * (pre-render scripts, auth redirect shells). Matches the public helper
 * described in `UX/colors.md:110`.
 */
export function applyTenantBranding(brand: ThemeBrand): void {
  if (typeof document === "undefined") return;
  const overrides = buildOverrides(brand);
  const root = document.documentElement;
  for (const [key, value] of Object.entries(overrides)) {
    root.style.setProperty(key, value);
  }
}
